// ---------------------------------------------------------------------------
// What a credential row MEANS on a screen. Pure functions, no React, no DOM and
// no colour — so every rule below is assertable under `node --test` rather than
// by looking at a screenshot. Same shape as system/navigation.ts and
// design/tone.ts, and for the same reason.
//
// THE RULE THIS FILE EXISTS TO KEEP: NOTHING HERE NAMES A VERIFICATION METHOD.
//
// The obvious implementation compares the method id against the two literal
// names that count. That is a hard-coded taxonomy (absolute rule 4) and it is
// the kind that rots quietly: the day a jurisdiction adds a fifth level, every
// screen goes on rendering the old answer and V8's gate — which reads
// app.verification_method.satisfies_blocking from the database — starts
// disagreeing with what the console shows. A worker would appear valid and be
// refused at assignment, with nothing on either screen explaining why.
//
// The two names are deliberately not written down here even as an example.
// verify-V7.sh greps web/src for every id in app.verification_method, and the
// guard cannot tell prose from code — nor should it try, since a per-file
// exemption is how a guard stops guarding (V3 finding 4, V4 finding 3). This
// comment was reworded rather than the check weakened.
//
// So the strength of a method arrives as DATA, from
// GET /v1/reference/verification-methods, which reads the same column the
// database's own app.credential_status() reads. One source of truth, two
// consumers.
//
// AND THE SECOND RULE: A SUPERSEDED ROW IS NOT A FAILURE. History accumulates
// beside the current row by design (09-... §V7.1), so a bench with five years of
// renewals must not look like a bench covered in red.
// ---------------------------------------------------------------------------

import type { Tone } from "../design/tone";
import { english, type Translate } from "../i18n/translate";

export type CredentialTypeFacts = {
  is_blocking: boolean;
  warn_days: number;
};

export type CredentialFacts = {
  is_current: boolean;
  superseded_by: string | null;
  expires_on: string | null;
  verification_method: string;
};

export type MethodFacts = {
  id: string;
  satisfies_blocking: boolean;
};

export type CredentialVerdict = {
  tone: Tone;
  /** The words on the chip. Never derived from the tone — see design/tone.ts. */
  label: string;
  /**
   * One plain sentence saying why, for the person looking at it.
   *
   * ABSOLUTE RULE 6 APPLIED EARLY. This verdict is the input to V17's Readiness
   * Score and V8's block, and both of those are automated decisions about a
   * person's work. Building the explanation at the same time as the rule — not
   * three slices later — is what stops the number arriving before the sentence.
   */
  reason: string;
};

/** Days between two ISO dates (YYYY-MM-DD), positive when `to` is later. */
export function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return Number.NaN;
  return Math.round((b - a) / 86_400_000);
}

/**
 * The verdict for ONE credential row, as of `today` (an ISO date).
 *
 * `today` is a PARAMETER AND NOT `new Date()`. A function that reads the clock
 * cannot be tested at a boundary, and every interesting case here is a
 * boundary: the day it expires, the day the warning starts.
 */
export function credentialVerdict(
  credential: CredentialFacts,
  type: CredentialTypeFacts | undefined,
  methods: readonly MethodFacts[],
  today: string,
  t: Translate = english,
): CredentialVerdict {
  // HISTORY IS NEUTRAL. It is not a problem, it is the record.
  if (!credential.is_current) {
    return credential.superseded_by
      ? { tone: "neutral", label: t("Superseded"), reason: t("This record was replaced by a newer one.") }
      : { tone: "neutral", label: t("Withdrawn"), reason: t("This record was withdrawn and not replaced.") };
  }

  const method = methods.find((m) => m.id === credential.verification_method);
  const blocking = type?.is_blocking === true;

  // EXPIRY FIRST, because an expired credential is a harder fact than an
  // unverified one and a person triaging a bench needs the worst news first.
  if (credential.expires_on) {
    const remaining = daysBetween(today, credential.expires_on);
    if (!Number.isNaN(remaining)) {
      if (remaining < 0) {
        return {
          tone: "failed",
          label: t("Expired"),
          reason: t("This expired on {date}.", { date: credential.expires_on }),
        };
      }
      // A method too weak for a BLOCKING credential is a failure; on a
      // non-blocking one it is merely worth flagging. The same row, two
      // different verdicts, and the difference is configuration.
      if (blocking && method && !method.satisfies_blocking) {
        return {
          tone: "failed",
          label: t("Not verified"),
          reason: t("Nobody has checked this against the document or the issuing register."),
        };
      }
      if (remaining <= (type?.warn_days ?? 0)) {
        return {
          tone: "attention",
          label: remaining === 0 ? t("Expires today") : t("Expires in {n} days", { n: remaining }),
          reason: t("This expires on {date}.", { date: credential.expires_on }),
        };
      }
    }
  }

  if (blocking && method && !method.satisfies_blocking) {
    return {
      tone: "failed",
      label: t("Not verified"),
      reason: t("Nobody has checked this against the document or the issuing register."),
    };
  }

  // A method the browser has never heard of is NOT assumed to be strong. The
  // list is fetched from the database, so an unknown id means the fetch failed
  // or the vocabulary moved — and in either case the safe reading of an absent
  // value is the restrictive one, the same reading rbac.ts makes of an absent
  // sharing id.
  if (!method) {
    return {
      tone: "neutral",
      label: t("Unknown check"),
      reason: t("We could not work out how this was checked."),
    };
  }

  if (!method.satisfies_blocking) {
    return {
      tone: "attention",
      label: t("Recorded only"),
      reason: t("This is recorded but nobody has verified it. It cannot satisfy a required credential."),
    };
  }

  return {
    tone: "live",
    label: t("Valid"),
    reason: credential.expires_on
      ? t("Checked, and valid until {date}.", { date: credential.expires_on })
      : t("Checked, and does not expire."),
  };
}

/**
 * Whether a worker satisfies a blocking credential type THROUGH A GIVEN DATE.
 *
 * THE DATE IS REQUIRED AND IT IS NOT TODAY. 09-... §V8.2: a licence expiring on
 * the 14th is perfectly valid when somebody is assigned on the 1st to work the
 * 20th, and every naive implementation checks the present moment and passes.
 * Making the horizon a required parameter means a caller cannot express the
 * wrong question — the same reason app.credential_status() takes `p_through`.
 *
 * This is the BROWSER'S copy of that answer, used to explain a blocked state
 * before the person submits. The database's version is the one that decides;
 * this one exists so the screen can say why in advance rather than after a 409.
 */
export function satisfiesThrough(
  credentials: readonly CredentialFacts[],
  credentialTypeId: string,
  credentialTypeOf: (c: CredentialFacts) => string,
  methods: readonly MethodFacts[],
  through: string,
): { satisfied: boolean; reason: string } {
  const current = credentials.find(
    (c) => c.is_current && credentialTypeOf(c) === credentialTypeId,
  );
  if (!current) {
    return { satisfied: false, reason: "There is no record of this credential." };
  }
  const method = methods.find((m) => m.id === current.verification_method);
  if (!method || !method.satisfies_blocking) {
    return {
      satisfied: false,
      reason: "This is recorded but nobody has checked it against the document or the register.",
    };
  }
  if (current.expires_on && daysBetween(current.expires_on, through) > 0) {
    return {
      satisfied: false,
      reason: `This expires on ${current.expires_on}, before the work finishes on ${through}.`,
    };
  }
  return { satisfied: true, reason: "Checked, and valid for the whole of the work." };
}

/**
 * Sweep S21b. The PATCH body for a credential type's edit form: only what changed.
 *
 * A field the form left alone is not sent, so a value the form does not show (the key)
 * is never written back from a stale copy. Empty = nothing to save, and the form keeps
 * its button disabled. S40 added validity, document required and retired; a blank
 * number field means "leave it", not zero.
 */
export function credentialTypeChanges(
  before: {
    name: string;
    required_verification_method: string;
    is_blocking: boolean;
    warn_days: number;
    validity_months: number | null;
    document_required: boolean;
    is_retired: boolean;
  },
  form: {
    name: string;
    method: string;
    blocking: boolean;
    warnDays: string;
    validityMonths: string;
    documentRequired: boolean;
    retired: boolean;
  },
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};
  const name = form.name.trim();
  if (name !== before.name) changes.name = name;
  if (form.method !== before.required_verification_method) changes.required_verification_method = form.method;
  if (form.blocking !== before.is_blocking) changes.is_blocking = form.blocking;
  if (form.warnDays !== "" && Number(form.warnDays) !== before.warn_days) changes.warn_days = Number(form.warnDays);
  if (form.validityMonths !== "" && Number(form.validityMonths) !== before.validity_months) {
    changes.validity_months = Number(form.validityMonths);
  }
  if (form.documentRequired !== before.document_required) changes.document_required = form.documentRequired;
  if (form.retired !== before.is_retired) changes.is_retired = form.retired;
  return changes;
}
